import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, Chip, Divider,
  Alert, CircularProgress
} from '@mui/material';
import { ArrowBack, Download, Visibility, CheckCircle, PictureAsPdf } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import StatusChip from '../common/StatusChip';

const InfoRow = ({ label, value, mono }) => value == null || value === '' ? null : (
  <Box sx={{ display: 'flex', py: 1, borderBottom: '1px solid #f5f5f5', gap: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Box>
);

export default function RemittanceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rem, setRem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/remittances/${id}`)
      .then(r => { setRem(r.data.data); setLoading(false); })
      .catch(() => { setError('Remittance not found'); setLoading(false); });
  }, [id]);

  const openPdf = async (download = false) => {
    try {
      const filename = rem.kotak_advice_path.split('/').pop();
      const res = await api.get(`/remittances/kotak-advice/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { setError('Could not load PDF'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error || !rem) return <Alert severity="error" sx={{ m: 3 }}>{error || 'Not found'}</Alert>;

  const totalDebit = (parseFloat(rem.inr_amount || 0) + parseFloat(rem.bank_charges || 0) + parseFloat(rem.bank_charges_gst || 0) + parseFloat(rem.bank_charges_fcc_gst || 0));

  return (
    <Box>
      <PageHeader
        title={rem.kotak_reference || rem.remittance_number}
        subtitle="Outward Remittance Detail"
        breadcrumbs={[{ label: 'Remittances', path: '/remittances' }, { label: rem.kotak_reference || rem.remittance_number }]}
      />

      <Grid container spacing={2.5}>
        {/* Left: Remittance info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Transaction Details</Typography>
              <InfoRow label="Kotak Master Ref"  value={rem.kotak_reference}    mono />
              <InfoRow label="Portal Ref (FT)"   value={rem.remittance_number}  mono />
              <InfoRow label="Remittance Date"   value={rem.remittance_date} />
              <InfoRow label="Completion Date"   value={rem.completion_date} />
              <InfoRow label="Beneficiary"       value={rem.beneficiary_name} />
              <InfoRow label="Purpose"           value={rem.purpose_description || rem.purpose_code} />
              <InfoRow label="Notes"             value={rem.notes} />
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <StatusChip status={rem.status} />
                <Chip
                  size="small"
                  label={(rem.remittance_status || 'open') === 'open' ? 'Open' : 'Closed'}
                  color={(rem.remittance_status || 'open') === 'open' ? 'success' : 'default'}
                  variant={(rem.remittance_status || 'open') === 'open' ? 'filled' : 'outlined'}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Financial breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Financial Details</Typography>
              <InfoRow label="Currency"         value={rem.currency} />
              <InfoRow label="Amount"           value={`${rem.currency} ${parseFloat(rem.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} />
              <InfoRow label="Exchange Rate"    value={rem.exchange_rate ? `₹ ${parseFloat(rem.exchange_rate).toFixed(4)} / USD` : null} />
              <InfoRow label="INR Principal"    value={rem.inr_amount ? `₹ ${parseFloat(rem.inr_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : null} />
              <Divider sx={{ my: 1 }} />
              <InfoRow label="Bank Charges"         value={rem.bank_charges ? `₹ ${parseFloat(rem.bank_charges).toFixed(2)}` : null} />
              <InfoRow label="GST on Charges"       value={rem.bank_charges_gst ? `₹ ${parseFloat(rem.bank_charges_gst).toFixed(2)}` : null} />
              <InfoRow label="FCC-GST on Charges"   value={rem.bank_charges_fcc_gst ? `₹ ${parseFloat(rem.bank_charges_fcc_gst).toFixed(2)}` : null} />
              {totalDebit > 0 && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.light' }}>
                  <Typography variant="body2" color="text.secondary">Total Account Debit</Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    ₹ {totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Kotak Credit Advice PDF */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>Kotak Credit Advice</Typography>
                {rem.kotak_advice_path && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<Visibility />} onClick={() => openPdf(false)}>
                      View PDF
                    </Button>
                    <Button variant="outlined" size="small" color="secondary" startIcon={<Download />} onClick={() => openPdf(true)}>
                      Download
                    </Button>
                  </Box>
                )}
              </Box>
              {rem.kotak_advice_path ? (
                <Alert severity="success" icon={<CheckCircle />}>
                  Credit advice PDF available — {rem.kotak_advice_path.split('/').pop()}
                </Alert>
              ) : (
                <Alert severity="info" icon={<PictureAsPdf />}>
                  No Kotak credit advice PDF uploaded yet. Use "Import Kotak PDF" on the remittances list to attach one.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
